import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { stripMarkdown } from '../utils/stripMarkdown';
import { ArrowLeftIcon, PaperAirplaneIcon, BookOpenIcon, SparklesIcon, ChatBubbleLeftRightIcon, ChartBarIcon, MicrophoneIcon, StopCircleIcon } from '@heroicons/react/24/outline';
import VoiceModeOverlay from '../components/VoiceModeOverlay';
import MarkdownMessage from '../components/MarkdownMessage';
import './ChatbotView.css';

function ChatbotView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chatbot, setChatbot] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [sessionMsgCount, setSessionMsgCount] = useState(0);
  const [chatbotConfig, setChatbotConfig] = useState({});
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  // Voice state
  const [recording, setRecording]       = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking]         = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const currentAudioRef  = useRef(null);
  const isRecordingRef   = useRef(false);
  const audioCtxRef      = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const storageKey = `chatconv_test_${id}`;

  // Persist conversationId to localStorage whenever it changes
  const saveConversationId = (convId) => {
    setConversationId(convId);
    if (convId) localStorage.setItem(storageKey, String(convId));
  };

  useEffect(() => {
    fetchChatbot();
    restoreSession();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatbot = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/chatbots/${id}`);
      const bot = response.data.chatbot;
      setChatbot(bot);
      setChatbotConfig(bot.config || {});
      document.title = `${bot.name} — Test Chat`;
    } catch (error) {
      toast.error('Failed to load chatbot');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Reset title on unmount
  useEffect(() => {
    return () => { document.title = 'AI Chatbot'; };
  }, []);

  const restoreSession = async () => {
    const savedId = localStorage.getItem(storageKey);
    if (!savedId) return;
    try {
      const res = await axios.get(`/api/chat/${id}/conversation/${savedId}`);
      const history = res.data.messages.map(m => ({ ...m }));
      if (history.length > 0) {
        setMessages(history);
        setConversationId(parseInt(savedId, 10));
      }
    } catch {
      // Conversation no longer exists — start fresh
      localStorage.removeItem(storageKey);
    }
  };

  const handleNewChat = () => {
    localStorage.removeItem(storageKey);
    setConversationId(null);
    setMessages([]);
    setSessionMsgCount(0);
    inputRef.current?.focus();
  };

  // ── Voice helpers ──────────────────────────────────────────────────────────

  const voiceEnabled = chatbotConfig?.voice_enabled === true;
  const ttsProvider  = chatbotConfig?.tts_provider || 'browser';

  const speakResponse = async (text) => {
    if (ttsProvider === 'none' || !text) return;
    window.speechSynthesis?.cancel();
    currentAudioRef.current?.pause();

    if (ttsProvider === 'openai') {
      try {
        setSpeaking(true);
        const res = await fetch(`/api/voice/${id}/speak`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text,
            voice: chatbotConfig?.tts_voice || 'nova',
            speed: chatbotConfig?.tts_speed || 1.0,
          }),
        });
        if (!res.ok) throw new Error();
        const blob  = await res.blob();
        const audio = new Audio(URL.createObjectURL(blob));
        currentAudioRef.current = audio;
        audio.onended = () => setSpeaking(false);
        audio.onerror = () => setSpeaking(false);
        audio.play();
      } catch { setSpeaking(false); }
    } else {
      const utt = new SpeechSynthesisUtterance(text);
      utt.rate  = chatbotConfig?.tts_speed || 1.0;
      utt.onstart = () => setSpeaking(true);
      utt.onend   = () => setSpeaking(false);
      utt.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utt);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType =
        MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
        MediaRecorder.isTypeSupported('audio/webm')             ? 'audio/webm' :
        MediaRecorder.isTypeSupported('audio/mp4')              ? 'audio/mp4' :
        MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')  ? 'audio/ogg;codecs=opus' :
        '';

      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      if (audioCtx.state === 'suspended') await audioCtx.resume();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      const freqData = new Uint8Array(analyser.frequencyBinCount);

      const SPEECH_THRESHOLD  = 20;
      const SILENCE_THRESHOLD = 15;
      const SILENCE_DURATION  = 2000;
      const MIN_RECORD_MS     = 1000;
      const MAX_RECORD_MS     = 60000;
      let silenceStart   = null;
      let speechDetected = false;
      const startedAt    = Date.now();

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        audioCtx.close();
        const actualMime = recorder.mimeType || mimeType || 'audio/webm';
        transcribeAudio(new Blob(audioChunksRef.current, { type: actualMime }));
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      isRecordingRef.current   = true;
      setRecording(true);

      const checkSilence = () => {
        if (!isRecordingRef.current) return;
        analyser.getByteFrequencyData(freqData);
        const rms = Math.sqrt(freqData.reduce((s, v) => s + v * v, 0) / freqData.length);
        const now = Date.now();
        if (now - startedAt >= MAX_RECORD_MS) { stopRecording(); return; }
        if (rms >= SPEECH_THRESHOLD) {
          speechDetected = true;
          silenceStart   = null;
        } else if (speechDetected && rms < SILENCE_THRESHOLD) {
          if (!silenceStart) silenceStart = now;
          else if (now - silenceStart >= SILENCE_DURATION && now - startedAt >= MIN_RECORD_MS) {
            stopRecording(); return;
          }
        } else {
          silenceStart = null;
        }
        requestAnimationFrame(checkSilence);
      };
      requestAnimationFrame(checkSilence);

    } catch (err) {
      alert('Could not start recording: ' + (err.message || 'Unknown error') + '\nPlease allow microphone access in your browser settings.');
    }
  };

  const stopRecording = () => {
    isRecordingRef.current = false;
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const transcribeAudio = async (blob) => {
    setTranscribing(true);
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 25000);
    try {
      const fd = new FormData();
      const ext = (blob.type || 'audio/webm').split('/')[1]?.split(';')[0] || 'webm';
      fd.append('audio', blob, `recording.${ext}`);
      const res  = await fetch(`/api/voice/${id}/transcribe`, { method: 'POST', body: fd, signal: controller.signal });
      clearTimeout(timeoutId);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Transcription failed');
      if (data.text?.trim()) {
        handleSend(null, data.text.trim());
      } else {
        toast.error("Couldn't hear anything — please try again.");
      }
    } catch (err) {
      clearTimeout(timeoutId);
      toast.error(err.name === 'AbortError' ? 'Transcription timed out — please try again.' : 'Transcription failed: ' + err.message);
    } finally {
      setTranscribing(false);
    }
  };

  // ── Chat send ──────────────────────────────────────────────────────────────

  const handleSend = async (e, forcedText = null) => {
    if (e) e.preventDefault();
    const userMessage = forcedText || inputMessage.trim();
    if (!userMessage || sending) return;
    if (!forcedText) setInputMessage('');
    setSending(true);

    // Add user message to UI immediately
    setMessages(prev => [...prev, { role: 'user', content: userMessage, created_at: new Date().toISOString() }]);

    try {
      const response = await axios.post(`/api/chat/${id}`, {
        message: userMessage,
        conversation_id: conversationId,
        user_session_id: 'test-session'
      });

      saveConversationId(response.data.conversation_id);
      setSessionMsgCount(prev => prev + 1);

      // Add AI response
      const aiMessage = {
        role: 'assistant',
        content: response.data.message,
        context_used: response.data.context_used || false,
        tokens_used: response.data.tokens_used,
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);
      if (forcedText) speakResponse(stripMarkdown(response.data.message));
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.error === 'limit_exceeded'
        ? data.message
        : (data?.message || 'Failed to send message');
      toast.error(msg, { duration: data?.error === 'limit_exceeded' ? 7000 : 4000 });
      // Remove user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!chatbot) {
    return null;
  }

  return (
    <div className="chatbot-view">
      {voiceModeOpen && (
        <VoiceModeOverlay
          chatbotId={id}
          chatbotName={chatbot.name}
          chatbotConfig={chatbotConfig}
          conversationId={conversationId}
          sessionId="test-session"
          onConversationId={saveConversationId}
          onClose={() => setVoiceModeOpen(false)}
        />
      )}
      <div className="chat-header">
        <div className="container">
          <div className="header-content">
            <button onClick={() => navigate('/dashboard')} className="back-button">
              <ArrowLeftIcon className="icon" />
              Back
            </button>
            {voiceEnabled && (
              <button className="btn-voice-mode-cv" onClick={() => setVoiceModeOpen(true)} title="Switch to voice mode">
                🎙 Voice Mode
              </button>
            )}
            {messages.length > 0 && (
              <button className="btn-new-chat" onClick={handleNewChat} title="Clear history and start a new conversation">
                + New Chat
              </button>
            )}
            <div className="chatbot-info">
              <h1>{chatbot.name}</h1>
              {chatbot.description && <p>{chatbot.description}</p>}
            </div>
            <div className="chat-stats-strip">
              <div className="chat-stat-item">
                <span className="chat-stat-value">{chatbot.total_conversations ?? 0}</span>
                <span className="chat-stat-label">Conversations</span>
              </div>
              <div className="chat-stat-sep" />
              <div className="chat-stat-item">
                <span className="chat-stat-value">{chatbot.messages_this_month ?? 0}</span>
                <span className="chat-stat-label">Msgs this month</span>
              </div>
              <div className="chat-stat-sep" />
              <div className="chat-stat-item">
                <span className="chat-stat-value">{chatbot.total_messages ?? 0}</span>
                <span className="chat-stat-label">All-time msgs</span>
              </div>
              <div className="chat-stat-sep" />
              <div className="chat-stat-item">
                <span className="chat-stat-value">
                  {chatbot.last_active
                    ? new Date(chatbot.last_active).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                    : '—'}
                </span>
                <span className="chat-stat-label">Last active</span>
              </div>
              {sessionMsgCount > 0 && (
                <>
                  <div className="chat-stat-sep" />
                  <div className="chat-stat-item session-stat">
                    <span className="chat-stat-value">{sessionMsgCount}</span>
                    <span className="chat-stat-label">This session</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="chat-container">
        <div className="chat-messages">
          {messages.length === 0 ? (
            <div className="empty-chat">
              <p>Start a conversation with {chatbot.name}</p>
              <small>Type a message below to begin chatting</small>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={index}
                className={`message ${message.role === 'user' ? 'user-message' : 'ai-message'}`}
              >
                <div className="message-content">
                  {message.role === 'assistant'
                    ? <MarkdownMessage content={message.content} />
                    : message.content}
                </div>
                <div className="message-meta">
                  <span className="message-time">
                    {new Date(message.created_at).toLocaleTimeString()}
                  </span>
                  {message.role === 'assistant' && (
                    <span className={`rag-badge ${message.context_used ? 'rag-hit' : 'rag-miss'}`}>
                      {message.context_used
                        ? <><BookOpenIcon className="rag-icon" /> Knowledge Base</>
                        : <><SparklesIcon className="rag-icon" /> AI Generated</>
                      }
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="message ai-message">
              <div className="message-content typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          {(recording || transcribing || speaking) && (
            <div className="cv-voice-status">
              {recording    && <><span className="cv-voice-dot" />Listening…</>}
              {transcribing && <><span className="cv-voice-dot cv-vd-yellow" />Transcribing…</>}
              {speaking     && <><span className="cv-voice-dot cv-vd-green" />Speaking…</>}
            </div>
          )}
          <form onSubmit={handleSend} className="chat-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              className="chat-input"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={recording ? 'Listening… click mic to stop' : 'Type your message...'}
              disabled={sending || transcribing}
            />
            {voiceEnabled && (
              <button
                type="button"
                className={`cv-mic-btn ${recording ? 'cv-mic-recording' : ''} ${transcribing ? 'cv-mic-busy' : ''}`}
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing || sending || speaking}
                title={recording ? 'Stop recording' : 'Voice input'}
              >
                {transcribing
                  ? <span className="cv-mic-spinner" />
                  : recording
                    ? <StopCircleIcon style={{ width: 20, height: 20, color: '#ef4444' }} />
                    : <MicrophoneIcon style={{ width: 20, height: 20, color: '#64748b' }} />}
              </button>
            )}
            <button
              type="submit"
              className="send-button"
              disabled={!inputMessage.trim() || sending}
            >
              <PaperAirplaneIcon className="icon" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChatbotView;



